import { parseMoney } from '@/shared/lib/money';

/**
 * Pure line/invoice calculation helpers.
 *
 * PowerApps formula, verified against live data:
 *   subtotal              = monto * cantidad
 *   subtotalDescontado    = subtotal * (1 - descuento/100)
 *   iva                   = subtotalDescontado * (iva/100)
 *   total                 = subtotalDescontado + iva
 *
 * `esRegalo = true` forces total to 0 (client pays nothing); subtotal
 * is still reported at list price so the gift value remains visible
 * in the pre-discount column.
 */

/**
 * IVA rate as a percentage. Country packs whitelist which rates are
 * valid for input; this type is loose to support both AR (21 / 10.5)
 * and MX (16 / 8) without each consumer enumerating them.
 */
export type IvaRate = number;

export type LineInput = {
  monto: number;
  cantidad: number;
  /** 0..100 */
  descuento: number;
  iva: IvaRate;
  esRegalo?: boolean;
};

export type LineTotals = {
  subtotal: number;
  subtotalDescontado: number;
  ivaAmount: number;
  total: number;
};

export function calcLineTotals(line: LineInput): LineTotals {
  const subtotal = line.monto * line.cantidad;
  if (line.esRegalo) {
    return { subtotal, subtotalDescontado: 0, ivaAmount: 0, total: 0 };
  }
  const subtotalDescontado = subtotal * (1 - line.descuento / 100);
  const ivaAmount = subtotalDescontado * (line.iva / 100);
  const total = subtotalDescontado + ivaAmount;
  return { subtotal, subtotalDescontado, ivaAmount, total };
}

export type InvoiceTotals = {
  subtotal: number;
  subtotalDescontado: number;
  /** Per-rate IVA buckets keyed by the rate's string form (e.g. `"21"`,
   * `"10.5"`, `"16"`, `"8"`). 0-rate lines are excluded. */
  ivaByRate: Record<string, number>;
  /** Sum of all non-zero IVA across lines. */
  ivaTotal: number;
  /**
   * AR-shaped backwards-compat fields. Always 0 outside AR. New code
   * should consume `ivaByRate` instead — these are kept for legacy
   * call sites that read `iva21` / `iva105` directly.
   */
  iva21: number;
  iva105: number;
  total: number;
};

export function calcInvoiceTotals(lines: LineInput[]): InvoiceTotals {
  const ivaByRate: Record<string, number> = {};
  const acc: InvoiceTotals = {
    subtotal: 0,
    subtotalDescontado: 0,
    ivaByRate,
    ivaTotal: 0,
    iva21: 0,
    iva105: 0,
    total: 0,
  };
  for (const line of lines) {
    const t = calcLineTotals(line);
    acc.subtotal += t.subtotal;
    acc.subtotalDescontado += t.subtotalDescontado;
    if (t.ivaAmount > 0 && line.iva > 0) {
      const key = String(line.iva);
      ivaByRate[key] = (ivaByRate[key] ?? 0) + t.ivaAmount;
      acc.ivaTotal += t.ivaAmount;
    }
    if (line.iva === 21) acc.iva21 += t.ivaAmount;
    else if (line.iva === 10.5) acc.iva105 += t.ivaAmount;
    acc.total += t.total;
  }
  return acc;
}

/** Round to 2 decimals (avoid fp drift when posting to SharePoint). */
export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Parse an IVA rate string (`"21"`, `"10,5"`, `"16"`, `"21%"`,
 * `"IVA Exento"`, etc.) into a numeric percentage. Returns 0 for empty,
 * exempt, or unparseable input — callers can layer a country-specific
 * fallback if 0 is not the right default for the form.
 */
export function parseIvaRateString(raw: string | null | undefined): IvaRate {
  const s = (raw ?? '').toString().trim();
  if (!s) return 0;
  if (/exento/i.test(s)) return 0;
  const n = Number(s.replace('%', '').replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Monto de una Nota de Crédito en la moneda de la venta. Una NC E (exportación) guarda
 * `Monto_CP` en PESOS equivalentes (lo que leen las vistas en pesos) y su importe real en
 * dólares en `CbteImporte_CP` (`CbteMonedaId_CP` = 'DOL'); en una venta en USD hay que usar
 * ese. Las NC A/B/C son siempre en pesos: `Monto_CP`.
 */
export function montoNcEnMonedaVenta(f: {
  Monto_CP?: string | null;
  CbteImporte_CP?: string | null;
  CbteMonedaId_CP?: string | null;
}): number {
  if (f.CbteMonedaId_CP === 'DOL') return parseMoney(f.CbteImporte_CP) ?? 0;
  return parseMoney(f.Monto_CP) ?? 0;
}

/**
 * Saldo a facturar de una venta, con las Notas de Crédito parciales tratadas
 * como DESCUENTO (decisión del cliente 2026-07-21): lo acreditado NO vuelve a
 * quedar pendiente de facturar, la venta simplemente se factura por menos.
 *
 * La NC se descuenta de los dos lados — del total facturable y de lo facturado
 * — así que se cancela y el saldo termina siendo `ventaTotal − sumFacturado`.
 * Se deja escrita la resta completa porque es lo que se muestra en pantalla
 * (`VerFacturasDialog`: "Total a facturar" y "Facturado" ya vienen netos).
 *
 * Ojo: sólo entran acá las NC PARCIALES. Una NC por el total reabre su factura
 * (el cobro vuelve a `facturado=false` y sale de `sumFacturado`), con lo cual
 * ese monto vuelve al saldo por sí solo y NO debe contarse en `sumNotasCredito`
 * — de ahí que el llamador filtre por factura-padre todavía adjunta.
 */
export function calcSaldoAFacturar({
  ventaTotal,
  sumFacturado,
  sumNotasCredito,
}: {
  ventaTotal: number;
  sumFacturado: number;
  sumNotasCredito: number;
}): { totalFacturable: number; sumFacturadoNeto: number; saldo: number } {
  const totalFacturable = round2(ventaTotal - sumNotasCredito);
  const sumFacturadoNeto = round2(sumFacturado - sumNotasCredito);
  return {
    totalFacturable,
    sumFacturadoNeto,
    saldo: round2(totalFacturable - sumFacturadoNeto),
  };
}

/**
 * ¿Esta fila de `04.ComprobantePagos` cuenta para el TECHO de facturación?
 *
 * Se alimenta de la lista COMPLETA (`listComprobantesByOperacion`), no de la
 * activa: `listActiveComprobantesByOperacion` descarta los 'Anulado' antes de
 * que ninguna suma los vea, y con Facturación Previa (AR) una factura viva
 * puede quedar sobre un cobro anulado — anular es un soft-delete de UNA
 * columna, `cae` y `nro_comprobante` sobreviven.
 *
 * Reaplicamos las mismas exclusiones que la lista activa y levantamos SÓLO la
 * de 'Anulado', y únicamente para las filas que todavía tienen factura
 * emitida. Si no, anular un cobro facturado devolvía su monto al techo y se
 * podía facturar dos veces la misma plata, con dos CAE reales. Una NC total
 * limpia `Facturado_CP` en el padre, así que ahí la fila deja de contar sola.
 *
 * Una función para los dos consumidores (el techo de la pantalla de Pagos y el
 * "saldo a facturar" de Ver Facturas) — antes eran dos copias y sólo se
 * arreglaba una.
 */
export function cuentaParaTechoFacturacion(f: {
  Status_P?: string | null;
  Facturado_CP?: string | null;
  EsFacturaInterna_CP?: string | null;
  MedioPago_CP?: string | null;
}): boolean {
  const status = (f.Status_P ?? '').trim();
  // Las NC son crédito fiscal, no cobros: nunca suman techo.
  if (status === 'Nota de Credito') return false;
  // Factura Interna (MX) y los pivotes Nova no son cobros del cliente.
  if (f.EsFacturaInterna_CP === 'SI') return false;
  if (f.MedioPago_CP === 'Cuentas por Cobrar - Nova') return false;
  if (f.MedioPago_CP === 'Cuentas por Pagar - Nova') return false;
  if (status === 'Anulado') return f.Facturado_CP === 'SI';
  return true;
}
